import { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import { OxygenNonDeliveryReason, oxygenNonDeliveryReasons } from '../../../../constants/oxygenNonDeliveryReasons'
import { OxygenDeliveryDto } from '../../../../store/services/dmeOrder';

interface OxygenDeliveryModalProps {
  show: boolean
  onHide: () => void
  onSave: (data: OxygenDeliveryDto) => void
  orderId: string
  patientName: string
  oxygenDeliveryDate?: Date | null
  oxygenNonDeliveryReason?: string | null
  deliveryType?: string
}

const findReasonOption = (reasonText: string | null | undefined): OxygenNonDeliveryReason | null => {
  if (!reasonText) return null
  const predefined = oxygenNonDeliveryReasons.find((r) => r.label === reasonText)
  return predefined || oxygenNonDeliveryReasons.find((r) => r.label.toLowerCase() === 'other') || null
}

const isCustomOtherText = (reasonText: string | null | undefined): boolean => {
  if (!reasonText) return false
  return !oxygenNonDeliveryReasons.find((r) => r.label === reasonText)
}

const getOtherText = (reasonText: string | null | undefined): string => {
  return isCustomOtherText(reasonText) ? reasonText || '' : ''
}

const getInitialState = (data: OxygenDeliveryDto) => {
  const { oxygenNonDeliveryReason, oxygenDeliveryDate } = data;

  const hasDeliveryDate = !!oxygenDeliveryDate
  const hasNonDeliveryReason = !!oxygenNonDeliveryReason

  return {
    delivered: hasDeliveryDate ? true : hasNonDeliveryReason ? false : null,
    deliveredDate: oxygenDeliveryDate || '',
    nonDeliveryReason: findReasonOption(oxygenNonDeliveryReason),
    otherReasonText: getOtherText(oxygenNonDeliveryReason),
  }
}

export const OxygenDeliveryModal = ({
  show,
  onHide,
  onSave,
  orderId,
  patientName,
  oxygenDeliveryDate,
  oxygenNonDeliveryReason,
  deliveryType = 'O2',
}: OxygenDeliveryModalProps) => {

  const initialState = getInitialState({ oxygenDeliveryDate, oxygenNonDeliveryReason })
  
  const [delivered, setDelivered] = useState<boolean | null>(initialState.delivered)
  const [deliveredDate, setDeliveredDate] = useState(initialState.deliveredDate)
  const [nonDeliveryReason, setNonDeliveryReason] = useState<OxygenNonDeliveryReason | null>(initialState.nonDeliveryReason)
  const [otherReasonText, setOtherReasonText] = useState(initialState.otherReasonText)

  useEffect(() => {
    const state = getInitialState({ oxygenDeliveryDate, oxygenNonDeliveryReason })
    setDelivered(state.delivered)
    setDeliveredDate(state.deliveredDate)
    setNonDeliveryReason(state.nonDeliveryReason)
    setOtherReasonText(state.otherReasonText)
  }, [oxygenDeliveryDate, oxygenNonDeliveryReason])

  const handleSave = () => {
    if (delivered === null) {
      toast.error('Please select Yes or No')
      return
    }

    if (delivered && !deliveredDate) {
      toast.error('Please select a delivery date')
      return
    }

    if (!delivered && !nonDeliveryReason) {
      toast.error('Please select a non-delivery reason')
      return
    }

    if (!delivered && nonDeliveryReason?.label.toLowerCase() === 'other' && !otherReasonText.trim()) {
      toast.error('Please specify the reason')
      return
    }

    const data = delivered
      ? { oxygenDeliveryDate: String(deliveredDate) }
      : { 
          oxygenNonDeliveryReason: nonDeliveryReason!.label.toLowerCase() === 'other' 
            ? otherReasonText.trim() 
            : nonDeliveryReason!.label 
        }
 
    onSave(data)
  }

  const handleCancel = () => {
    const state = getInitialState({ oxygenDeliveryDate, oxygenNonDeliveryReason })
    setDelivered(state.delivered)
    setDeliveredDate(state.deliveredDate)
    setNonDeliveryReason(state.nonDeliveryReason)
    setOtherReasonText(state.otherReasonText)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header className="px-4">
        <Modal.Title>Edit {deliveryType} Delivery Status</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="border px-4">
          <div className="my-3">
            <div className="mb-2">
              <strong>Order ID:</strong> {orderId}
            </div>
            <div>
              <strong>Patient Name:</strong> {patientName}
            </div>
          </div>
          <hr className="w-100 mt-0" />

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Has {deliveryType} been delivered to the patient?
                <span className="text-danger"> *</span>
              </Form.Label>
              <div className="d-flex flex-column pl-1">
                <Form.Check
                  type="radio"
                  id="delivered-yes"
                  label="Yes"
                  checked={delivered === true}
                  onChange={() => setDelivered(true)}
                  className="pb-1"
                />
                <Form.Check
                  type="radio"
                  id="delivered-no"
                  label="No"
                  checked={delivered === false}
                  onChange={() => setDelivered(false)}
                />
              </div>
            </Form.Group>

            {delivered === true && (
              <Form.Group className="mb-3">
                <Form.Label>
                  {deliveryType} Delivered Date:
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={String(deliveredDate)}
                  onChange={(e) => setDeliveredDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </Form.Group>
            )}

            {delivered === false && (
              <>
                <Form.Group className="d-flex flex-column mb-3">
                  <Form.Label>
                    {deliveryType} Non-Delivery Reason:
                    <span className="text-danger"> *</span>
                  </Form.Label>
                  <ReactSelect
                    className="basic-single w-100 mt-2 mr-3"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Choose a reason..."
                    onChange={(value) => {
                      setNonDeliveryReason(value)
                      if (value?.label.toLowerCase() !== 'other') {
                        setOtherReasonText('')
                      }
                    }}
                    options={oxygenNonDeliveryReasons}
                    value={
                      oxygenNonDeliveryReasons?.find(
                        (reason) => nonDeliveryReason?.value === reason?.value,
                      ) ?? null
                    }
                  />
                </Form.Group>

                {nonDeliveryReason?.label.toLowerCase() === 'other' && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Please specify the reason:
                      <span className="text-danger"> *</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={otherReasonText}
                      onChange={(e) => setOtherReasonText(e.target.value.slice(0, 100))}
                      placeholder="Enter reason (max 100 characters)..."
                      maxLength={100}
                    />
                    <Form.Text className="text-muted">
                      {otherReasonText.length}/100 characters
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            )}
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
